import { getClient } from '@reservoir0x/relay-sdk';
import { useCallback, useEffect, useState } from 'react';
import { GetQuoteParametersWithoutChainId } from '@/common/types';
import { enableRelayMint, getMintRelayParameters } from '@/lib/relay';
import useMainStore from '@/store/mainStore';

interface RelayFees {
  bridgeFee: {
    raw?: string;
    formatted?: string;
    formattedUSD?: string;
  };
  gasFee: {
    raw?: string;
    formatted?: string;
    formattedUSD?: string;
  };
  total: {
    raw?: string;
    formatted?: string;
    formattedUSD?: string;
  };
}

interface UseMintPricesResult {
  fees: Record<string, RelayFees | null>;
  loading: Record<string, boolean>;
  fetchFees: () => void;
  error: Record<string, string | null>;
}

/**
 * Hook to get the relay fees for a given call config
 * @param availableNetworks - The networks to get the fees for
 * @param callConfig - The Relay call config
 * @param userBalance - The user's balance, if specified, we will check if the user has enough funds to pay for the fees
 * @returns The relay fees for a given call config
 */
export function useRelayFees(
  availableNetworks: number[],
  parameters: GetQuoteParametersWithoutChainId,
): UseMintPricesResult {
  const [fees, setFees] = useState<Record<string, RelayFees | null>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<Record<string, string | null>>({});

  const fetchFees = useCallback(async () => {
    // get quote for each networks
    availableNetworks.forEach(async (networkId) => {
      console.log('fetching fees', networkId);
      if (!parameters) return;
      setLoading((prev) => ({ ...prev, [networkId]: true }));
      setError((prev) => ({ ...prev, [networkId]: null }));
      try {
        const quote = await getClient().actions.getQuote({
          ...parameters,
          chainId: networkId,
        });

        setFees((prev) => ({
          ...prev,
          [networkId]: {
            bridgeFee: {
              raw: quote.fees?.relayer?.amount,
              formatted: quote.fees?.relayer?.amountFormatted,
              formattedUSD: quote.fees?.relayer?.amountUsd,
            },
            gasFee: {
              raw: quote.fees?.gas?.amount,
              formatted: quote.fees?.gas?.amountFormatted,
              formattedUSD: quote.fees?.gas?.amountUsd,
            },
          },
        }));
      } catch (e) {
        console.error(e);

        setFees((prev) => ({ ...prev, [networkId]: null }));
        setError((prev) => ({
          ...prev,
          [networkId]: e instanceof Error ? e.message : 'Failed to fetch price',
        }));
      } finally {
        setLoading((prev) => ({ ...prev, [networkId]: false }));
      }
    });
  }, [parameters]);

  useEffect(() => {
    fetchFees();
  }, [parameters]);

  return { fees, loading, error, fetchFees };
}

/**
 * Hook to get the Relay call config for a given claim
 * @returns The Relay call config for a given claim
 */
export function useMintRelayParameters() {
  const studioInstance = useMainStore((state) => state.studioInstance);
  const walletAddress = useMainStore((state) => state.walletAddress);
  const mintToAddress = useMainStore((state) => state.mintToAddress);
  const numberOfMints = useMainStore((state) => state.numberOfMints);
  const connectedNetwork = useMainStore((state) => state.connectedNetwork);
  const [parameters, setParameters] = useState<GetQuoteParametersWithoutChainId>();
  useEffect(() => {
    async function fetchCallConfig() {
      if (!studioInstance || !enableRelayMint(studioInstance)) {
        return;
      }

      const recipient = mintToAddress?.toLowerCase() || walletAddress?.toLowerCase();
      if (!recipient || !connectedNetwork) {
        return;
      }
      const claimCallConfig = await getMintRelayParameters({
        chainId: connectedNetwork,
        studioInstance,
        recipient,
        numberOfMints,
        withSigner: true,
      });
      setParameters(claimCallConfig);
    }
    fetchCallConfig();
  }, [studioInstance, mintToAddress, walletAddress, numberOfMints]);

  return {
    parameters,
  };
}
